/**
 * BRAND ASSETS MANIFEST — the single source of truth for every brand file
 * this app references. Founder Msg 71 point 5 & 6.
 *
 * Rules:
 * - Every component references a slot on this manifest. NEVER a hardcoded path.
 * - NEVER an inline SVG of the mark, the compass, a rose, or a badge.
 * - When a file is missing on disk the app renders `<AssetPlaceholder slot=… />`
 *   which shows the exact expected filename. A wrong logo that looks plausible
 *   is worse than an obvious gap.
 * - The four Iko tiles (patsherehe / patpolite / patmichezo / patkazi) are
 *   ALREADY DIMMED to 25%. Never re-apply opacity in CSS.
 * - Score badge slots exist for future wiring only. The score engine is NOT
 *   being built yet — do not use the badge slots.
 */

// Path base — resolved by CRA public/ (serves /app/frontend/public/assets/brand)
const ASSET_ROOT = "/assets/brand";

// Slot -> { expected filename (as delivered), path (public URL), ground }
export const BRAND_ASSETS = {
  // Lockups
  "logo.horizontal.night": {
    filename: "logo_horizontal_fullcolour-white-type_1024.png",
    path: `${ASSET_ROOT}/logos/logo_horizontal_fullcolour-white-type_1024.png`,
    ground: "night",
  },
  "logo.horizontal.cream": {
    filename: "logo_horizontal_fullcolour-ink-type_1024.png",
    path: `${ASSET_ROOT}/logos/logo_horizontal_fullcolour-ink-type_1024.png`,
    ground: "cream",
  },
  "logo.horizontaltight.night": {
    filename: "logo_horizontaltight_fullcolour-white-type_1024.png",
    path: `${ASSET_ROOT}/logos/logo_horizontaltight_fullcolour-white-type_1024.png`,
    ground: "night",
  },
  "logo.horizontaltight.cream": {
    filename: "logo_horizontaltight_fullcolour-ink-type_1024.png",
    path: `${ASSET_ROOT}/logos/logo_horizontaltight_fullcolour-ink-type_1024.png`,
    ground: "cream",
  },
  // Mark-only — 1024 for headers, 512 for small (<96px) placements per founder Msg 72
  "logo.mark": {
    filename: "logo_mark_fullcolour-transparent_1024.png",
    path: `${ASSET_ROOT}/logos/logo_mark_fullcolour-transparent_1024.png`,
    ground: "any",
  },
  "logo.mark.512": {
    filename: "logo_mark_fullcolour-transparent_512.png",
    path: `${ASSET_ROOT}/logos/logo_mark_fullcolour-transparent_512.png`,
    ground: "any",
  },
  // App icons + avatar — always on ink ground, even on a cream page
  "logo.appicon": {
    filename: "logo_appicon_on-ink_1024.png",
    path: `${ASSET_ROOT}/logos/logo_appicon_on-ink_1024.png`,
    ground: "ink",
  },
  "logo.appicon.rounded": {
    filename: "logo_appicon-rounded_on-ink_1024.png",
    path: `${ASSET_ROOT}/logos/logo_appicon-rounded_on-ink_1024.png`,
    ground: "ink",
  },
  "logo.avatar": {
    filename: "logo_avatar_on-ink_1024.png",
    path: `${ASSET_ROOT}/logos/logo_avatar_on-ink_1024.png`,
    ground: "ink",
  },
  // Favicons — each drawn for its size, NEVER a downscale
  "favicon.32":  { filename: "logo_favicon_on-ink_32.png",  path: `${ASSET_ROOT}/logos/logo_favicon_on-ink_32.png`,  ground: "ink" },
  "favicon.48":  { filename: "logo_favicon_on-ink_48.png",  path: `${ASSET_ROOT}/logos/logo_favicon_on-ink_48.png`,  ground: "ink" },
  "favicon.180": { filename: "logo_favicon_on-ink_180.png", path: `${ASSET_ROOT}/logos/logo_favicon_on-ink_180.png`, ground: "ink" },
  "favicon.512": { filename: "logo_favicon_on-ink_512.png", path: `${ASSET_ROOT}/logos/logo_favicon_on-ink_512.png`, ground: "ink" },
  // Patterns — read-under rosette in 2048 (headers) + 1024 (small surfaces),
  // fabric in 1024 (only cover/launch use, brief 14% on cover).
  "pattern.rosette.night": {
    filename: "pattern_rosette_2048.png",
    path: `${ASSET_ROOT}/patterns/pattern_rosette_2048.png`,
    ground: "night",
  },
  "pattern.rosette.night.1024": {
    filename: "pattern_rosette_1024.png",
    path: `${ASSET_ROOT}/patterns/pattern_rosette_1024.png`,
    ground: "night",
  },
  "pattern.fabric.night": {
    filename: "pattern_fabric_1024.png",
    path: `${ASSET_ROOT}/patterns/pattern_fabric_1024.png`,
    ground: "night",
  },
  // Iko feel tiles — pre-dimmed to 25%, do not re-apply opacity in CSS.
  "tile.sherehe": { filename: "patsherehe.png", path: `${ASSET_ROOT}/patterns/patsherehe.png`, ground: "any", predimmed: true },
  "tile.polite":  { filename: "patpolite.png",  path: `${ASSET_ROOT}/patterns/patpolite.png`,  ground: "any", predimmed: true },
  "tile.michezo": { filename: "patmichezo.png", path: `${ASSET_ROOT}/patterns/patmichezo.png`, ground: "any", predimmed: true },
  "tile.kazi":    { filename: "patkazi.png",    path: `${ASSET_ROOT}/patterns/patkazi.png`,    ground: "any", predimmed: true },
  // W-C.33 (Vania 7 Oct 2026 · correction) · rubric illustrations · the
  // masters live in Vania's Google Drive (03 Design › Assets › Rubric
  // illustrations › Live on the website). Four files, one per iko, at
  // /assets/brand/illustrations/ill-vibe-{iko}.jpeg (1264×848 master kept
  // untouched). We ALSO ship a web derivative at ill-vibe-{iko}.web.jpeg
  // (800px wide, JPEG q78 progressive, ~55-67KB each) that L2.1 uses so
  // the four-tile slide downloads under 300KB instead of 3.3MB. BRAND
  // ASSET RULE (Vania 7 Oct 2026): brand assets come from Vania's Drive
  // masters only. Never scraped from the live site. Never pulled from
  // another app. Web derivatives are generated from the master in-repo
  // via PIL (see /app/memory/brand-assets-rule.md).
  "illustration.vibe.sherehe": {
    filename: "ill-vibe-sherehe.jpeg",
    path: `${ASSET_ROOT}/illustrations/ill-vibe-sherehe.jpeg`,
    ground: "any",
    alt: "a day party crowd dancing, arms up, under a low sun",
  },
  "illustration.vibe.polite": {
    filename: "ill-vibe-polite.jpeg",
    path: `${ASSET_ROOT}/illustrations/ill-vibe-polite.jpeg`,
    ground: "any",
    alt: "a seated audience listening, a steward at the side",
  },
  "illustration.vibe.michezo": {
    filename: "ill-vibe-michezo.jpeg",
    path: `${ASSET_ROOT}/illustrations/ill-vibe-michezo.jpeg`,
    ground: "any",
    alt: "a stand on its feet, one scarf held overhead",
  },
  "illustration.vibe.kazi": {
    filename: "ill-vibe-kazi.jpeg",
    path: `${ASSET_ROOT}/illustrations/ill-vibe-kazi.jpeg`,
    ground: "any",
    alt: "a room mid session, lanyards on",
  },

  // W-C.34 (Vania 7 Oct 2026) · brand photography for onboarding event cards.
  // Masters live in Vania's Drive (03 Design › Assets › Photography). Three
  // files per the brief · 1408×768. Masters live at /assets/brand/photography
  // untouched; the GuestIntro slides read a web derivative generated via PIL
  // (800px wide JPEG ≈ 60KB). Both slots registered so BrandAsset consumers
  // can pick either. When the masters are missing, the img uses onError to
  // fall back to the iko pattern tile and the layout stays intact.
  "photography.sherehe.peak_01": {
    filename: "photo_sherehe_peak_01.png",
    path: `${ASSET_ROOT}/photography/photo_sherehe_peak_01.png`,
    web:  `${ASSET_ROOT}/photography/photo_sherehe_peak_01.web.jpeg`,
    ground: "any",
    alt: "a club floor, hands up, pink light",
    derivation: "web = 800px wide, JPEG q78 progressive, generated via PIL from the Drive master",
  },
  "photography.michezo.speed_02": {
    filename: "photo_michezo_speed_02.png",
    path: `${ASSET_ROOT}/photography/photo_michezo_speed_02.png`,
    web:  `${ASSET_ROOT}/photography/photo_michezo_speed_02.web.jpeg`,
    ground: "any",
    alt: "a player on the ball, the crowd behind the fence",
    derivation: "web = 800px wide, JPEG q78 progressive, generated via PIL from the Drive master",
  },
  "photography.polite.gallery_02": {
    filename: "photo_polite_gallery_02.png",
    path: `${ASSET_ROOT}/photography/photo_polite_gallery_02.png`,
    web:  `${ASSET_ROOT}/photography/photo_polite_gallery_02.web.jpeg`,
    ground: "any",
    alt: "a gallery, people standing with the art",
    derivation: "web = 800px wide, JPEG q78 progressive, generated via PIL from the Drive master",
  },
  // Roses — Msg 73 + D39 (Vania 20 Sep · reconfirmed W-C.21 7 Oct). Every
  // rating displayed uses these SVGs verbatim · never redrawn, recoloured
  // or restretched. Only AWARDED roses are drawn. No empty slot. No faded
  // grey placeholder. No keyline. Below 3.0 the row is not drawn at all.
  // The "empty" / "outline-empty" variants are kept in the manifest for
  // historical delivery but MUST NOT be referenced by any live surface.
  ...Object.fromEntries(
    ["full", "half", "empty", "outline-full", "outline-half", "outline-empty"].flatMap((state) =>
      (state.startsWith("outline-")
        ? ["yellow", "ink", "white"]
        : ["yellow", "ink", "white", "cream"]
      ).map((cw) => [
        `rose.${state}.${cw}`,
        {
          filename: `rose_${state}_${cw}.svg`,
          path: `${ASSET_ROOT}/roses/rose_${state}_${cw}.svg`,
          ground: "any",
        },
      ]),
    ),
  ),
  // Badge slots — DEFINED, UNUSED. Score engine is not built. Filenames follow
  // the delivered convention: badge_{level}_{variant}_{context}_{size}.png
  // e.g. badge_4-5_full_night_128.png. Do NOT reference these anywhere yet.
};

/**
 * Pick the right lockup slot for the current width + ground (founder Msg 71 point 6).
 *   ≥ 320px  → horizontal
 *   160–320  → horizontal (same file, smaller render)
 *   96–160   → horizontaltight
 *   24–96    → mark.512 (small file drawn for the small size, brief Msg 72)
 *   < 24     → favicon.32 (use the smallest-drawn favicon in the app itself)
 */
export function lockupSlotForWidth(widthPx, ground /* "night" | "cream" */) {
  if (widthPx >= 160) return `logo.horizontal.${ground}`;
  if (widthPx >= 96)  return `logo.horizontaltight.${ground}`;
  if (widthPx >= 24)  return "logo.mark.512";
  return "favicon.32";
}
